/* Wishlist — guest mode.

   Audit 14.3 (decision reversed 6 Aug): localStorage is the source of truth. The
   heart works instantly for everyone, with no login wall and no network round
   trip, which is what removes the dependency on logged_in_customer_id — a value
   Shopify documents as unreliable with new customer accounts (1.3).

   Every card and PDP heart shares this, so state cannot disagree between two
   places showing the same product. Buttons are found by data-wishlist-id and
   their aria-pressed is rehydrated on load, which is the only state signal the
   design has (home.css:445 styles the filled heart off aria-pressed).

   Layer 2 of the audit's design — best-effort sync to a customer metafield
   through the App Proxy when a customer ID actually arrives — is deliberately not
   wired yet: the endpoint does not exist, and calling it would 404 on every tap.
   The place it belongs is marked below.
*/

(() => {
  if (window.CaratKinWishlist) return;

  const STORAGE_KEY = "carat_kin_wishlist";
  const HANDLES_KEY = "carat_kin_wishlist_handles";

  function read() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      // Private browsing and full storage both throw. A wishlist that cannot
      // persist should still not break the page.
      return [];
    }
  }

  /** Read the id→handle map. IDs without a handle are omitted. */
  function readHandles() {
    try {
      const raw = window.localStorage.getItem(HANDLES_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  function writeHandles(map) {
    try {
      window.localStorage.setItem(HANDLES_KEY, JSON.stringify(map));
    } catch {
      /* ignore */
    }
  }

  function write(ids) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch {
      /* ignore */
    }
    // Prune handles for IDs no longer in the list.
    const handles = readHandles();
    let pruned = false;
    for (const key of Object.keys(handles)) {
      if (!ids.includes(key)) { delete handles[key]; pruned = true; }
    }
    if (pruned) writeHandles(handles);

    document.dispatchEvent(new CustomEvent("carat-kin:wishlist-change", { detail: { ids } }));
    // TODO (audit 14.3 layer 2): when /apps/cart-kin/wishlist exists, push the
    // list here and merge on sign-in. Failure must stay silent — localStorage is
    // already authoritative.
  }

  function has(id) {
    return read().includes(String(id));
  }

  function toggle(id, handle) {
    const key = String(id);
    const ids = read();
    const index = ids.indexOf(key);
    if (index === -1) {
      ids.push(key);
      // Persist the handle so the wishlist page can fetch the product.
      if (handle) {
        const handles = readHandles();
        handles[key] = String(handle);
        writeHandles(handles);
      }
    } else {
      ids.splice(index, 1);
    }
    write(ids);
    return index === -1;
  }

  function paint(root = document) {
    const ids = read();
    root.querySelectorAll?.("[data-wishlist-id]").forEach((button) => {
      const saved = ids.includes(String(button.dataset.wishlistId));
      button.setAttribute("aria-pressed", String(saved));
      const title = button.dataset.wishlistTitle;
      if (title) {
        button.setAttribute("aria-label", saved ? `Remove ${title} from wishlist` : `Add ${title} to wishlist`);
      }
    });
  }

  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-wishlist-id]");
    if (!button) return;
    // The heart sits over the card's product link. Prevent the default follow
    // and stop the click bubbling so no card-level handler navigates instead of
    // toggling the wishlist.
    event.preventDefault();
    event.stopPropagation();
    // Saving needs a signed-in customer (auth-gate.js); removing never does.
    if (
      !has(button.dataset.wishlistId) &&
      window.CaratKinAuthGate?.intercept({
        kind: "wishlist",
        id: String(button.dataset.wishlistId),
        handle: button.dataset.wishlistHandle || "",
      }, () => button.click())
    ) return;
    toggle(button.dataset.wishlistId, button.dataset.wishlistHandle);
    paint();
  });

  // Any card added later — a filtered grid, a section reload, a rendered feed —
  // gets its saved state painted on.
  document.addEventListener("carat-kin:wishlist-change", () => paint());
  document.addEventListener("shopify:section:load", (event) => paint(event.target));

  window.CaratKinWishlist = { read, readHandles, has, toggle, paint, count: () => read().length };
  paint();
})();
